import { useEffect, useRef, useState } from 'react';
import type { Cue } from '@core/model/cue.ts';
import {
  TRANSLATION_SEEK_SETTLE_MS,
  applyMemberOnlyGate,
  selectCuesForTranslationRequest,
  shouldDeferTranslationFetch,
} from '@core/translation/scheduler.ts';
import type { TranslationProvider } from '@ports/TranslationProvider.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import type { SubtitleStore, SubtitleSlot } from '../state/subtitleStore.ts';
import { resolveTranslationProvider } from '../adapters/translation/index.ts';
import {
  runBatchTranslate,
} from '../adapters/translation/batchTranslate.ts';
import {
  applyTranslationFailureToCues,
  classifyTranslationError,
} from '../adapters/translation/translationFailure.ts';
import { translationMemberOnlyText } from '../lib/serviceStatusMessages.ts';
import type { MembershipState } from './useMembership.ts';

export const DEFAULT_TRANSLATION_WINDOW_SECONDS = 10;
export const TRANSLATION_POLL_MS = 500;
export const MEMBER_ONLY_TEXT = 'Member only feature';

/** Extension translationService.*.seconds parity. */
export function translationWindowSecondsForEngine(engine: string): number {
  switch (engine) {
    case 'azure':
    case 'deepl':
      return 3;
    case 'ai':
      return 15;
    case 'google':
    default:
      return DEFAULT_TRANSLATION_WINDOW_SECONDS;
  }
}

export type TranslationUiStatus = 'idle' | 'busy' | 'error';

export type UseTranslationOptions = {
  /** YouTube video id — required for AI Batch jobs. */
  videoId?: string;
  /** Override window ±seconds while playing (else per-engine defaults). */
  windowSeconds?: number;
  pollMs?: number;
  memberOnlyText?: string;
  /** Injected providers (tests); otherwise resolved from settings.engine. */
  provider?: TranslationProvider;
  googleProvider?: TranslationProvider;
  memberProvider?: TranslationProvider;
  /** Force-invalidate subtitle sync after in-place cue writes. */
  onInvalidate?: () => void;
  /** Clock for settle debounce (tests). */
  now?: () => number;
  /** Override interval (tests). */
  setIntervalFn?: typeof setInterval;
  clearIntervalFn?: typeof clearInterval;
};

function targetLangForSlot(
  slot: SubtitleSlot,
  mainTranslations: string[],
  secondTranslations: string[],
): string | undefined {
  const list = slot === 'main' ? mainTranslations : secondTranslations;
  const lang = list[0];
  return lang && lang !== 'null' ? lang : undefined;
}

/**
 * Polling translation loop — ports extension scheduler semantics into React.
 * When engine is `batch`, runs a one-shot Sora full-track translate job per slot/lang.
 */
export function useTranslation(
  video: VideoController | null,
  store: SubtitleStore,
  settingsStore: SettingsStore,
  membership: MembershipState,
  options: UseTranslationOptions = {},
): { status: TranslationUiStatus } {
  const seekingRef = useRef(false);
  const settleUntilRef = useRef(0);
  const inFlightRef = useRef(false);
  const failCountRef = useRef(0);
  const batchDoneRef = useRef(new Set<string>());
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [status, setStatus] = useState<TranslationUiStatus>('idle');

  const pollMs = options.pollMs ?? TRANSLATION_POLL_MS;
  const memberOnlyText = options.memberOnlyText ?? translationMemberOnlyText();

  const unlock = membership.unlock;
  const userId = membership.userId;
  const userToken = membership.userToken;

  useEffect(() => {
    batchDoneRef.current.clear();
  }, [options.videoId]);

  useEffect(() => {
    if (!video) return;
    const offs = [
      video.on('seeking', () => {
        seekingRef.current = true;
      }),
      video.on('seeked', () => {
        seekingRef.current = false;
        const now = optionsRef.current.now ?? Date.now;
        settleUntilRef.current = now() + TRANSLATION_SEEK_SETTLE_MS;
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [video]);

  useEffect(() => {
    if (!video) {
      setStatus('idle');
      return;
    }

    const tick = async () => {
      const opts = optionsRef.current;
      const now = opts.now ?? Date.now;
      if (inFlightRef.current) return;
      if (
        shouldDeferTranslationFetch({
          seeking: seekingRef.current,
          settleUntilMs: settleUntilRef.current,
          nowMs: now(),
        })
      ) {
        return;
      }

      const settings = settingsStore.getSnapshot?.() ?? (await settingsStore.getAll());
      const engine = settings.translationEngine || 'google';
      const slots: SubtitleSlot[] = ['main', 'second'];

      if (engine === 'batch') {
        if (!opts.videoId || !unlock) {
          for (const slot of slots) {
            const state = store.getSlot(slot);
            const cues = state.cues;
            if (!cues?.length) continue;
            const targetLang = targetLangForSlot(
              slot,
              settings.mainTranslations,
              settings.secondTranslations,
            );
            if (!targetLang) continue;
            if (!unlock) {
              applyMemberOnlyGate(cues, false, 'batch', memberOnlyText);
              opts.onInvalidate?.();
            }
          }
          return;
        }

        for (const slot of slots) {
          const state = store.getSlot(slot);
          const cues = state.cues;
          if (!cues || cues.length === 0) continue;
          const targetLang = targetLangForSlot(
            slot,
            settings.mainTranslations,
            settings.secondTranslations,
          );
          if (!targetLang) continue;
          const available = state.trackList?.translations?.map((tr) => tr.code) ?? [];
          if (available.length > 0 && !available.includes(targetLang)) continue;

          const doneKey = `${opts.videoId}:${slot}:${targetLang}:${state.selectedId ?? ''}`;
          if (batchDoneRef.current.has(doneKey)) {
            // Lang cleared then re-selected: cues are untranslated again — re-run.
            if (cues.every((c) => c.translated)) continue;
            batchDoneRef.current.delete(doneKey);
          }

          const trackId = state.selectedId ?? '0';

          inFlightRef.current = true;
          setStatus('busy');
          try {
            await runBatchTranslate({
              videoId: opts.videoId,
              trackId,
              targetLang,
              cues,
              isMember: unlock,
              userId,
              onProgress: () => {
                opts.onInvalidate?.();
              },
            });
            batchDoneRef.current.add(doneKey);
            failCountRef.current = 0;
            setStatus('idle');
            opts.onInvalidate?.();
          } catch (err) {
            applyTranslationFailureToCues(cues, classifyTranslationError(err));
            failCountRef.current += 1;
            setStatus('error');
            opts.onInvalidate?.();
          } finally {
            inFlightRef.current = false;
          }
        }
        return;
      }

      const provider =
        opts.provider
        ?? (opts.googleProvider && engine === 'google'
          ? opts.googleProvider
          : opts.memberProvider && engine !== 'google'
            ? opts.memberProvider
            : resolveTranslationProvider({
                engine,
                unlock,
                userId,
                userToken,
              }));

      const playing = !video.isPaused();
      const windowSeconds =
        opts.windowSeconds ?? translationWindowSecondsForEngine(engine);

      for (const slot of slots) {
        const state = store.getSlot(slot);
        const cues = state.cues;
        if (!cues || cues.length === 0) continue;

        const targetLang = targetLangForSlot(
          slot,
          settings.mainTranslations,
          settings.secondTranslations,
        );
        if (!targetLang) continue;
        const available = state.trackList?.translations?.map((tr) => tr.code) ?? [];
        if (available.length > 0 && !available.includes(targetLang)) continue;
        const to = targetLang;

        const currentTimeMs = video.getCurrentTimeMs() + state.view.delayMs;
        const selected = selectCuesForTranslationRequest(cues, currentTimeMs, windowSeconds, {
          prefetch: playing,
          pendingFlag: 'translated',
          expandOnHit: true,
        }) as Cue[];

        if (selected.length === 0) continue;

        if (applyMemberOnlyGate(selected, unlock, engine, memberOnlyText)) {
          opts.onInvalidate?.();
          continue;
        }

        const texts = selected.map((c) => c.text);
        inFlightRef.current = true;
        setStatus('busy');
        // Extension: only AI engine sets cue.loading (shows web_service_loading on overlay).
        if (engine === 'ai') {
          for (const cue of selected) cue.loading = true;
          opts.onInvalidate?.();
        }
        try {
          const results = await provider.translate(texts, to);
          for (let i = 0; i < selected.length; i += 1) {
            const cue = selected[i];
            if (!cue) continue;
            cue.translation = results[i] ?? '';
            cue.translated = true;
          }
          failCountRef.current = 0;
          setStatus('idle');
          opts.onInvalidate?.();
        } catch (err) {
          applyTranslationFailureToCues(selected, classifyTranslationError(err));
          failCountRef.current += 1;
          setStatus('error');
          opts.onInvalidate?.();
        } finally {
          if (engine === 'ai') {
            for (const cue of selected) cue.loading = false;
          }
          inFlightRef.current = false;
        }
      }
    };

    const setIntervalFn = options.setIntervalFn ?? setInterval;
    const clearIntervalFn = options.clearIntervalFn ?? clearInterval;
    const id = setIntervalFn(() => {
      void tick();
    }, pollMs);
    void tick();
    return () => clearIntervalFn(id);
  }, [video, store, settingsStore, unlock, userId, userToken, pollMs, memberOnlyText]);

  return { status };
}
